import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane } from 'react-icons/fa';
import { contactApi } from '../services/endpoints';
import { useTranslation } from 'react-i18next';
import { SUPPORT_EMAIL } from '../config';

const ContactSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const services = [
    'Smart Home Automation',
    'Security Systems',
    'Intelligent Lighting',
    'Climate Control',
    'CCTV Installation',
    'Other',
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await contactApi.submit(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
    } catch (err) {
      setError(t('contact.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: FaPhone,
      title: t('contact.phone'),
      content: '+40 743 691 021',
      link: 'tel:+40743691021',
    },
    {
      icon: FaEnvelope,
      title: t('contact.email'),
      content: SUPPORT_EMAIL,
      link: `mailto:${SUPPORT_EMAIL}`,
    },
    {
      icon: FaMapMarkerAlt,
      title: t('contact.location'),
      content: t('contact.locationValue'),
      link: '#',
    },
  ];

  return (
    <section
      id="contact"
      ref={ref}
      className="section-padding bg-gradient-to-b from-[#0f1640] to-[#0a0e27] relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <div className="inline-block glass px-6 py-2 rounded-full mb-6">
            <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
              {t('contact.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('contact.title')}
            <span className="gradient-text block mt-2">{t('contact.subtitle')}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t('contact.description')}
          </p>
        </motion.div>

        {/* Clean Contact Info Cards - Above Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          {contactInfo.map((info, index) => (
            <motion.a
              key={index}
              href={info.link}
              whileHover={{ y: -5 }}
              className="glass rounded-2xl p-6 text-center group"
            >
              <div className="w-14 h-14 rounded-xl bg-[#00d4ff]/20 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#00d4ff]/30 transition-colors">
                <info.icon className="text-[#00d4ff] text-2xl" />
              </div>
              <div className="text-white/60 text-sm mb-2">{info.title}</div>
              <div className="text-white font-medium group-hover:text-[#00d4ff] transition-colors">
                {info.content}
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* Simplified Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mx-auto"
        >
          <div className="glass-strong rounded-3xl p-8 md:p-12">
            <h3 className="text-3xl font-bold text-white mb-8 text-center">{t('contact.formTitle')}</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                    placeholder={t('contact.namePlaceholder')}
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                    placeholder={t('contact.emailPlaceholder')}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                    placeholder={t('contact.phonePlaceholder')}
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#00d4ff] transition-colors appearance-none"
                    data-testid="contact-service-select"
                  >
                    <option value="" className="bg-[#0a0e27]">{t('contact.servicePlaceholder')}</option>
                    {services.map((service) => (
                      <option key={service} value={service} className="bg-[#0a0e27]">
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                  data-testid="contact-message-input"
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 text-red-400 text-center">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-500/10 border border-green-500/50 rounded-xl p-4 text-green-400 text-center">
                  {t('contact.successMessage')}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary !py-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 text-lg"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <span>{t('contact.sending')}</span>
                ) : (
                  <>
                    <FaPaperPlane />
                    <span>{t('contact.submitButton')}</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Address Card & Interactive Map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6 }}
            className="mt-8 space-y-6"
          >
            {/* Address Card */}
            <div className="glass rounded-2xl p-6 text-center">
              <h4 className="text-lg font-bold text-white mb-3">{t('contact.visitUs')}</h4>
              <p className="text-white/70 text-sm leading-relaxed">
                {t('contact.addressLine1')}<br/>
                {t('contact.addressLine2')}<br/>
                {t('contact.addressLine3')}
              </p>
            </div>

            {/* Interactive Map */}
            <div className="glass-strong rounded-2xl overflow-hidden">
              <div className="relative w-full h-[400px] md:h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2848.6516584739944!2d26.102532615447983!3d44.43679170961688!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b1ff476025f1a1%3A0x3a7e3d1f3f0e1234!2sBucharest%2C%20Romania!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sheba Smart Home Location"
                  className="w-full h-full"
                />
                
                {/* Overlay with Get Directions Button */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0a0e27]/95 to-transparent p-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-center sm:text-left">
                      <h5 className="text-white font-semibold mb-1">{t('contact.mapTitle')}</h5>
                      <p className="text-white/60 text-sm">{t('contact.mapSubtitle')}</p>
                    </div>
                    <a
                      href="https://www.google.com/maps/dir//Bucharest,+Romania"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary whitespace-nowrap px-6 py-3"
                    >
                      <FaMapMarkerAlt className="inline-block mr-2" />
                      {t('contact.getDirections')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
